import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { useAuth } from "../context/AuthContext";
import { CalendarDays, Send, Check, ChevronDown, ChevronUp, Users, Sparkles, Share2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const fmtTime = (t) => {
  try {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ap}`;
  } catch {
    return t;
  }
};

const AvailabilityBrowser = ({ token, refreshKey }) => {
  const { current } = useAuth();
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pinged, setPinged] = useState({});

  const ping = async (id) => {
    setPinged((p) => ({ ...p, [id]: "busy" }));
    try {
      const res = await fetch(`${API}/api/availability/${id}/interested`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      setPinged((p) => ({ ...p, [id]: res.ok ? "ok" : "error" }));
    } catch {
      setPinged((p) => ({ ...p, [id]: "error" }));
    }
  };

  useEffect(() => {
    fetch(`${API}/api/availability${filter ? `?project=${encodeURIComponent(filter)}` : ""}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => { setRows(d.availability || []); setProjects(d.projects || []); })
      .catch(() => {});
  }, [token, filter, refreshKey]);

  return (
    <div className="avail-browser" data-testid="availability-browser">
      <h3 className="req-title"><Users size={16} /> Who's available</h3>
      <div className="avail-filter-chips">
        <button type="button" className={`avail-chip${filter === "" ? " selected" : ""}`}
          onClick={() => setFilter("")} data-testid="avail-filter-all">all</button>
        {projects.map((p) => (
          <button key={p} type="button" className={`avail-chip${filter === p ? " selected" : ""}`}
            onClick={() => setFilter(filter === p ? "" : p)}
            data-testid={`avail-filter-${p.replace(/ /g, "-")}`}>{p}</button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="feed-empty" data-testid="avail-browser-empty">
          {filter ? `Nobody has shared availability for ${filter} yet.` : "Nobody has shared availability yet — be the first!"}
        </div>
      ) : (
        <div className="avail-board">
          {rows.map((r) => (
            <div className="avail-card" key={r.id} data-testid={`avail-card-${r.id}`}>
              <Link to={`/members/${r.user_id}`} className="avail-card-name">{r.name}</Link>
              <div className="avail-card-projects">{(r.projects || []).join(" · ")}</div>
              <div className="avail-card-dates">
                {(r.dates || []).slice(0, 6).map(fmtDate).join(", ")}
                {(r.dates || []).length > 6 ? ` +${r.dates.length - 6} more` : ""}
              </div>
              <div className="avail-card-time">{fmtTime(r.time_from)}–{fmtTime(r.time_to)} · {r.cadence}</div>
              {r.notes && <div className="avail-card-notes">{r.notes}</div>}
              {current && r.user_id !== current.id && (
                pinged[r.id] === "ok" ? (
                  <div className="avail-pinged" data-testid={`avail-pinged-${r.id}`}><Check size={13} /> Pinged in chat!</div>
                ) : (
                  <button type="button" className="avail-interest-btn"
                    disabled={pinged[r.id] === "busy"}
                    onClick={() => ping(r.id)}
                    data-testid={`avail-interested-${r.id}`}>
                    <Sparkles size={13} /> {pinged[r.id] === "busy" ? "Pinging…" : pinged[r.id] === "error" ? "Try again" : "I'm interested"}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const INVITE_LINKS = [
  { key: "studio sessions", slug: "studio-sessions", label: "Musicians — recording sessions" },
  { key: "photoshoots", slug: "photoshoots", label: "Models — photoshoots" },
  { key: "photography", slug: "photography", label: "Photographers" },
  { key: "model casting", slug: "model-casting", label: "Model casting" },
  { key: "consulting", slug: "consulting", label: "Consultants" },
  { key: "website design", slug: "website-design", label: "Website designers" },
  { key: "graphic design", slug: "graphic-design", label: "Graphic designers" },
  { key: "fashion design", slug: "fashion-design", label: "Fashion designers" },
  { key: "game dev", slug: "game-dev", label: "Game devs" },
];

const SLUG_TO_PROJECT = Object.fromEntries(INVITE_LINKS.map((l) => [l.slug, l.key]));

export const AvailabilityCalendar = ({ initialProject = "", standalone = false }) => {
  const { token } = useAuth();
  const invitedProject = SLUG_TO_PROJECT[initialProject] || "";
  const [open, setOpen] = useState(standalone || !!invitedProject);
  const [projects, setProjects] = useState(invitedProject ? [invitedProject] : []);
  const [copiedLink, setCopiedLink] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const sectionRef = React.useRef(null);

  useEffect(() => {
    if (invitedProject && sectionRef.current) {
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
    }
  }, [invitedProject]);

  const copyInvite = async (l) => {
    const url = `${window.location.origin}/availability?project=${l.slug}`;
    const text = `Share your availability for ${l.key} with The Monde Mania: ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopiedLink(l.slug);
      setTimeout(() => setCopiedLink(""), 2000);
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        setCopiedLink(l.slug);
        setTimeout(() => setCopiedLink(""), 2000);
      } catch {
        window.prompt("Copy this invite link:", url);
      }
    }
  };
  const [allProjects, setAllProjects] = useState([]);
  const [dates, setDates] = useState([]);
  const [timeFrom, setTimeFrom] = useState("18:00");
  const [timeTo, setTimeTo] = useState("21:00");
  const [cadence, setCadence] = useState("week to week");
  const [notes, setNotes] = useState("");
  const [last, setLast] = useState(null);
  const [state, setState] = useState("idle");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token || !open) return;
    fetch(`${API}/api/availability/mine`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        setAllProjects(d.projects || []);
        if (d.availability) setLast(d.availability);
      })
      .catch(() => {});
  }, [token, open]);

  const toggleProject = (p) =>
    setProjects((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const submit = async (e) => {
    e.preventDefault();
    if (projects.length === 0 || dates.length === 0) return;
    setState("busy");
    try {
      const res = await fetch(`${API}/api/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({
          projects,
          dates: dates.map(toISO),
          time_from: timeFrom,
          time_to: timeTo,
          cadence,
          notes,
        }),
      });
      setState(res.ok ? "ok" : "error");
      if (res.ok) {
        setRefreshKey((k) => k + 1);
        setTimeout(() => setState("idle"), 4000);
      }
    } catch {
      setState("error");
    }
  };

  if (!token) {
    if (!standalone) return null;
    return (
      <section className="avail-section" data-testid="availability-section">
        <div className="avail-guest-note" data-testid="avail-guest-note">
          Anyone can browse who's open to collaborate below.{" "}
          <Link to="/members" data-testid="avail-guest-login-link">Log in or join</Link> to share your own availability and ping people.
        </div>
        <AvailabilityBrowser token={null} refreshKey={0} />
      </section>
    );
  }

  return (
    <section className="avail-section" data-testid="availability-section" ref={sectionRef}>
      {!standalone && (
        <button type="button" className="btn-ghost avail-toggle" onClick={() => setOpen(!open)} data-testid="availability-toggle">
          <CalendarDays size={16} /> Availability calendar {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
      {open && (
        <form className="req-form avail-form" onSubmit={submit} data-testid="availability-form">
          <h3 className="req-title">When can you work?</h3>
          {invitedProject && (
            <div className="avail-invited-note" data-testid="avail-invited-note">
              <Check size={14} /> You've been invited to share your availability for <strong>{invitedProject}</strong> — it's already selected below.
            </div>
          )}
          <p className="gd-upload-note">Pick the projects you want in on and the days/times that work — it posts to the member chat so others can link up with you.</p>
          <div className="avail-invite-row" data-testid="avail-invite-links">
            <button type="button" className="btn-ghost avail-share-btn"
              onClick={() => setShareOpen(!shareOpen)} data-testid="avail-share-toggle">
              <Share2 size={14} /> Share an invite link {shareOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {shareOpen && (
              <div className="avail-share-menu" data-testid="avail-share-menu">
                <p className="gd-upload-note">Send someone straight to the availability form with their project preselected:</p>
                <div className="avail-share-grid">
                  {INVITE_LINKS.map((l) => (
                    <button key={l.slug} type="button" className="avail-chip"
                      onClick={() => copyInvite(l)}
                      data-testid={`avail-invite-${l.slug}`}>
                      {copiedLink === l.slug ? "Link copied!" : l.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="req-field">
            <span className="req-label">Projects / requests you want to be part of*</span>
            <div className="gd-services-grid" data-testid="avail-projects">
              {allProjects.map((p) => (
                <label key={p} className={`gd-service-box${projects.includes(p) ? " selected" : ""}`}
                  data-testid={`avail-project-${p.replace(/ /g, "-")}`}>
                  <input type="checkbox" checked={projects.includes(p)} onChange={() => toggleProject(p)} />
                  <span>{p}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="avail-cal-row">
            <div className="req-field">
              <span className="req-label">Best days* ({dates.length} picked)</span>
              <div className="avail-calendar" data-testid="avail-calendar">
                <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d || [])}
                  numberOfMonths={1} fromDate={new Date()} />
              </div>
            </div>
            <div className="avail-side">
              <label className="req-field"><span className="req-label">From*</span>
                <input className="req-input" type="time" value={timeFrom} required
                  onChange={(e) => setTimeFrom(e.target.value)} data-testid="avail-time-from" /></label>
              <label className="req-field"><span className="req-label">Until*</span>
                <input className="req-input" type="time" value={timeTo} required
                  onChange={(e) => setTimeTo(e.target.value)} data-testid="avail-time-to" /></label>
              <label className="req-field"><span className="req-label">This repeats</span>
                <select className="req-input" value={cadence} onChange={(e) => setCadence(e.target.value)} data-testid="avail-cadence">
                  <option value="week to week">week to week</option>
                  <option value="month to month">month to month</option>
                </select></label>
              <label className="req-field"><span className="req-label">Notes</span>
                <textarea className="req-input" rows={2} value={notes} maxLength={500}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Anything else people should know…"
                  data-testid="avail-notes" /></label>
            </div>
          </div>
          {state === "ok" ? (
            <div className="shirt-claim-ok" data-testid="avail-ok"><Check size={16} /> Posted to the member chat!</div>
          ) : (
            <button className="btn-solid" type="submit"
              disabled={state === "busy" || projects.length === 0 || dates.length === 0}
              data-testid="avail-submit">
              <Send size={15} /> {state === "busy" ? "Posting…" : "Share my availability"}
            </button>
          )}
          {state === "error" && <div className="cart-error">Couldn't post — check your picks and try again.</div>}
          {last && (
            <p className="gd-upload-note" data-testid="avail-last">
              Last shared: {last.projects.join(", ")} · {last.dates.length} day{last.dates.length === 1 ? "" : "s"} ({last.cadence})
            </p>
          )}
        </form>
      )}
      {open && <AvailabilityBrowser token={token} refreshKey={refreshKey} />}
    </section>
  );
};
